import {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  useCreateContainerStatus,
  useDeleteContainerStatus,
  useGetContainerStatusesByType,
  useUpdateContainerStatus,
} from 'api/containerStatuses';
import { isSuccessful } from 'api/utils';
import { appToast } from 'utils/toast.utils';
import {
  NEW_ROW_ID,
  StatusDraft,
  StatusType,
  StatusRow,
  EMPTY_DRAFT,
} from '../components/Dialogs/Statuses/StatusesTable.types';
import { DIALOGS } from '../TransportCloudTable.consts';

export const useStatusesTable = (kind: StatusType) => {
  const { data, loading, getContainerStatusesByType } =
    useGetContainerStatusesByType();
  const { createContainerStatus, loading: creating } =
    useCreateContainerStatus();
  const { updateContainerStatus, loading: updating } =
    useUpdateContainerStatus();
  const { deleteContainerStatus } = useDeleteContainerStatus();

  const [rows, setRows] = useState<StatusRow[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<StatusDraft>({
    ...EMPTY_DRAFT,
    type: kind,
  });
  const [errors, setErrors] = useState<Record<keyof StatusDraft, boolean>>({
    name: false,
    color: false,
    type: false,
    enabled: false,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const saving = creating || updating || deleting;

  const loadRows = useCallback(
    () => getContainerStatusesByType(kind),
    [getContainerStatusesByType, kind],
  );

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  useEffect(() => {
    setRows(
      (data ?? []).map(({ id, name, color, enabled }) => ({
        id,
        type: kind,
        name,
        color,
        enabled: Boolean(enabled),
      })),
    );
  }, [data, kind]);

  const handleAdd = () => {
    setRows((prev) => [
      ...prev,
      { id: NEW_ROW_ID, type: kind, ...EMPTY_DRAFT },
    ]);
    setDraft({ ...EMPTY_DRAFT, type: kind });
    setErrors({ name: false, color: false, type: false, enabled: false });
    setEditingId(NEW_ROW_ID);
  };

  const handleStartEdit = (row: StatusRow) => {
    setDraft({
      type: row.type,
      name: row.name,
      color: row.color,
      enabled: row.enabled,
    });
    setErrors({ name: false, color: false, type: false, enabled: false });
    setEditingId(row.id);
  };

  const handleDraftChange = <F extends keyof StatusDraft>(
    field: F,
    value: StatusDraft[F],
  ) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const handleColorChange = async (row: StatusRow, color: string) => {
    if (editingId === row.id) {
      handleDraftChange('color', color);
      return;
    }
    const response = await updateContainerStatus(row.id, {
      name: row.name,
      type: kind,
      color,
      enabled: row.enabled,
    });
    if (response?.status && isSuccessful(response)) {
      appToast.success(DIALOGS.statusUpdateSuccessMessage);
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, color } : r)),
      );
    }
  };

  const handleToggleEnabled = async (row: StatusRow) => {
    if (editingId === row.id) {
      handleDraftChange('enabled', !draft.enabled);
      return;
    }
    const enabled = !row.enabled;
    const response = await updateContainerStatus(row.id, {
      name: row.name,
      type: kind,
      color: row.color,
      enabled,
    });
    if (response?.status && isSuccessful(response)) {
      appToast.success(DIALOGS.statusUpdateSuccessMessage);
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, enabled } : r)),
      );
    }
  };

  const handleConfirmEdit = async (id: number) => {
    if (!draft.name.trim()) {
      setErrors((prev) => ({ ...prev, name: true }));
      return;
    }
    const payload = {
      name: draft.name,
      type: kind,
      color: draft.color,
      enabled: Boolean(draft.enabled),
    };
    const isNew = id === NEW_ROW_ID;
    const response = isNew
      ? await createContainerStatus(payload)
      : await updateContainerStatus(id, payload);

    if (response?.status && isSuccessful(response)) {
      appToast.success(
        isNew
          ? DIALOGS.statusAddSuccessMessage
          : DIALOGS.statusUpdateSuccessMessage,
      );
      setEditingId(null);
      setDraft({ ...EMPTY_DRAFT, type: kind });
      await loadRows();
    }
  };

  const handleCancelEdit = () => {
    setRows((prev) => prev.filter((row) => row.id !== NEW_ROW_ID));
    setEditingId(null);
    setDraft({ ...EMPTY_DRAFT, type: kind });
    setErrors({ name: false, color: false, type: false, enabled: false });
  };

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    setSelected(
      event.target.checked
        ? rows.filter((row) => row.id !== NEW_ROW_ID).map((row) => row.id)
        : [],
    );
  };

  const handleSelect = (event: MouseEvent<HTMLButtonElement>, id: number) => {
    event.stopPropagation();
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      const responses = await Promise.all(
        selected.map((id) => deleteContainerStatus(id)),
      );
      const allSuccessful = responses.every(
        (response) => response?.status && isSuccessful(response),
      );
      if (allSuccessful) {
        appToast.success(
          selected.length > 1
            ? DIALOGS.statusDeleteSuccessMessageMultiple
            : DIALOGS.statusDeleteSuccessMessageSingle,
        );
        setSelected([]);
        await loadRows();
      }
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  return {
    rows,
    loading,
    saving,
    selected,
    hoveredId,
    setHoveredId,
    editingId,
    draft,
    errors,
    deleteDialogOpen,
    setDeleteDialogOpen,
    handleAdd,
    handleStartEdit,
    handleDraftChange,
    handleColorChange,
    handleToggleEnabled,
    handleConfirmEdit,
    handleCancelEdit,
    handleSelectAll,
    handleSelect,
    handleDeleteConfirm,
  };
};
